/**
 * GhlSubaccountsPanel — extension panel for the integrations hub that lists
 * every connected LeadConnector subaccount and lets the user disconnect each
 * independently or connect another one.
 *
 * Renders nothing when:
 *  - The user is not in the multi-GHL allowlist (single-account UX is enough).
 *  - GHL is not connected (the hub's own Connect button handles the first link).
 *
 * Self-fetches /api/integrations/crm/status; React Query shares the cache with
 * the hub's main status query when keys match.
 */
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { apiRequest } from "@/lib/queryClient";
import { escapeHtml } from "@/lib/utils";
import { Loader2, Plug, Unplug } from "lucide-react";

interface GhlLocationInfo {
  id: string;
  locationId: string;
  locationName?: string;
  companyId?: string;
  tokenExpiresAt?: string;
}

interface GhlStatusResponse {
  connected: boolean;
  locationId?: string;
  locationName?: string;
  locations?: GhlLocationInfo[];
  multiAccountAllowed?: boolean;
}

export function GhlSubaccountsPanel({ forUserId }: { forUserId?: string | null }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const fuParam = forUserId ? `?forUserId=${forUserId}` : "";
  // Match the hub's status query key so cache invalidations from one place
  // (eg. confirmDisconnect in IntegrationCard) refresh this panel too.
  const statusKey = forUserId ? ["hub-ghl", forUserId] : ["hub-ghl"];

  const status = useQuery<GhlStatusResponse>({
    queryKey: statusKey,
    queryFn: () => apiRequest("GET", `/api/integrations/crm/status${fuParam}`),
    staleTime: 60_000,
    retry: false,
  });

  const popupRef = useRef<Window | null>(null);

  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (event.data?.type === "GHL_OAUTH_SUCCESS") {
        queryClient.invalidateQueries({ queryKey: statusKey });
        queryClient.invalidateQueries({ queryKey: ["integrations-statuses"] });
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forUserId]);

  const connectMutation = useMutation({
    mutationFn: () => apiRequest("GET", `/api/integrations/crm/connect${fuParam}`),
    onSuccess: (data: { url: string }) => {
      if (popupRef.current && !popupRef.current.closed) {
        popupRef.current.location.href = data.url;
      }
    },
  });

  const disconnectMutation = useMutation({
    mutationFn: (locationId: string) => {
      const body: Record<string, string> = { locationId };
      if (forUserId) body.forUserId = forUserId;
      return apiRequest("POST", "/api/integrations/crm/disconnect", body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: statusKey });
      queryClient.invalidateQueries({ queryKey: ["integrations-statuses"] });
    },
  });

  const handleConnectAnother = () => {
    const w = 600,
      h = 700;
    const left = window.screenX + (window.outerWidth - w) / 2;
    const top = window.screenY + (window.outerHeight - h) / 2;
    popupRef.current = window.open(
      "about:blank",
      "ghl_oauth",
      `width=${w},height=${h},left=${left},top=${top},toolbar=no,menubar=no`
    );
    if (popupRef.current) {
      popupRef.current.document.write(
        '<html><head><title>LeadConnector</title></head><body style="background:#111;color:#fff;display:flex;align-items:center;justify-content:center;height:100vh;margin:0;font-family:sans-serif"><p>Łączenie z LeadConnector...</p></body></html>'
      );
      popupRef.current.document.close();
    }
    const checkInterval = setInterval(() => {
      if (popupRef.current?.closed) {
        clearInterval(checkInterval);
        queryClient.invalidateQueries({ queryKey: statusKey });
        queryClient.invalidateQueries({ queryKey: ["integrations-statuses"] });
      }
    }, 500);
    connectMutation.mutate();
  };

  if (status.isLoading || !status.data) return null;
  if (!status.data.connected) return null;
  if (!status.data.multiAccountAllowed) return null;

  const locations = status.data.locations ?? [];

  return (
    <div className="space-y-2 p-3 bg-white/[0.03] border border-white/[0.08] rounded-lg">
      <div className="text-[10px] uppercase tracking-wide text-white/40">
        {t("ghl.subaccountsTitle")}
      </div>
      <div className="space-y-1.5">
        {locations.map((loc) => (
          <div
            key={loc.id}
            className="flex items-center justify-between bg-white/[0.04] border border-white/[0.06] rounded-md px-2.5 py-1.5"
          >
            <span className="text-xs text-white/80 truncate">
              {loc.locationName || loc.locationId}
            </span>
            <button
              onClick={() => disconnectMutation.mutate(loc.locationId)}
              disabled={disconnectMutation.isPending}
              className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded transition-colors disabled:opacity-50"
            >
              {disconnectMutation.isPending && disconnectMutation.variables === loc.locationId ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <Unplug className="w-3 h-3" />
              )}
              {t("ghl.disconnectThis")}
            </button>
          </div>
        ))}
      </div>
      <button
        onClick={handleConnectAnother}
        disabled={connectMutation.isPending}
        className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-1.5 bg-orange-600/80 hover:bg-orange-600 text-white text-[11px] font-medium rounded transition-colors disabled:opacity-50"
      >
        {connectMutation.isPending ? (
          <Loader2 className="w-3 h-3 animate-spin" />
        ) : (
          <Plug className="w-3 h-3" />
        )}
        {t("ghl.connectAnother")}
      </button>
    </div>
  );
}
